/// <amd-module name="Build/SourceProviders/TfsGit" />
import { BaseGit } from "Build/SourceProviders/BaseGit";
import {
    IBranchInfo,
    ICommitDiffInfo,
    ICommitInfo,
    IContentInfo,
    IContentLinkData,
    IRepositoryInfo,
    RepositoryTypes
} from "Build/SourceProviders/Common";
import * as Resources from "Build/SourceProviders/Resources";
import { GitRepository } from "VC/Common/Contracts/Git";
import { IVCNavigationService } from "VC/Common/Navigation";
import { IGitRestClient } from "VC/Common/RestClient/Git";
import { startsWith } from "VSS/Core/Util/String";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

export class TfsGit extends BaseGit {
    private _gitClient: Promise<IGitRestClient> | undefined;
    private knownRepositories: { [id: string]: Promise<GitRepository> } = {};

    public getKey() {
        return RepositoryTypes.TfsGit;
    }

    public getName() {
        return Resources.TfsGit;
    }

    public getRepoIconName(): string {
        return "GitLogo";
    }

    public fetchBranchLink(info: IBranchInfo, projectName?: string) {
        return this.vcNavigationService.then(service => {
            return this._getBranchLink(info, service, projectName);
        });
    }

    public fetchCommitLink(info: ICommitInfo, projectName?: string) {
        return this.vcNavigationService.then(service => {
            return service.getGitCommitUrl(info.commitId, info.repositoryName || "", projectName);
        });
    }

    public fetchPRCommitLink(info: IBranchInfo, projectName?: string): Promise<string | null> {
        if (info.repositoryName && info.branchName) {
            const prId = this._getPullRequestId(info.branchName);
            if (prId) {
                return this.vcNavigationService.then(service => {
                    return `${service.getPullRequestUrl(prId, info.repositoryName, projectName)}?_a=commits`;
                });
            }
        }
        return this.emptyNullPromise;
    }

    public fetchCommitDiffLink(info: ICommitDiffInfo, projectName?: string): Promise<string | null> {
        return this.vcNavigationService.then(service => {
            return service.getGitCompareCommitsUrl(info.baseCommitId, info.targetCommitId, info.repositoryName || "", projectName);
        });
    }

    public fetchContentLinks(info: IContentInfo[]): Promise<IContentLinkData[] | null> {
        return this.vcNavigationService.then(service => {
            const linkData: IContentLinkData[] = [];
            (info || []).forEach(linkInfo => {
                const link = this._getContentLink(linkInfo, service);
                if (link) {
                    linkData.push({
                        id: linkInfo.id,
                        link: link
                    });
                }
            });

            return linkData;
        });
    }

    public fetchContentLink(info: IContentInfo) {
        if (info.repositoryName) {
            return this.vcNavigationService.then(service => {
                return this._getContentLink(info, service);
            });
        }

        return this.emptyNullPromise;
    }

    public fetchRepositoryLink(info: IRepositoryInfo, projectName?: string): Promise<string | null> {
        if (info.repositoryName) {
            return this.vcNavigationService.then(service => {
                return this._getRepositoryLink(info, service, projectName);
            });
        }
        return this.emptyNullPromise;
    }

    public getProjectName(repositoryId: string): Promise<string | null> {
        if(!userHasClaim(this.pageContext, UserClaims.Member)) {
            return Promise.resolve("");
        }

        if (!this.knownRepositories[repositoryId]) {
            this.knownRepositories[repositoryId] = this.gitClient.then(client => client.getRepository(repositoryId));
        }
        
        return this.knownRepositories[repositoryId].then(
            repo => repo.project && repo.project.name,
            () => null
        );
    }

    protected get gitClient(): Promise<IGitRestClient> {
        if (!this._gitClient) {
            this._gitClient = this.getVcCode().then(() => {
                return this.pageContext.getRestClient<IGitRestClient>("IGitRestClient");
            });
        }

        return this._gitClient;
    }

    protected _getBranchLink(info: IBranchInfo, service: IVCNavigationService, projectName?: string) {
        const prId = this._getPullRequestId(info.branchName);
        if (prId) {
            return service.getPullRequestUrl(prId, info.repositoryName, projectName);
        } else {
            return service.getRefUrl(info.branchName, info.repositoryName, projectName);
        }
    }

    protected _getCommitLink(info: ICommitInfo, service: IVCNavigationService, projectName?: string) {
        return service.getGitCommitUrl(info.commitId, info.repositoryName || "", projectName);
    }

    protected _getRepositoryLink(info: IRepositoryInfo, service: IVCNavigationService, projectName?: string): string {
        return service.getRepositoryUrl(info.repositoryName, projectName);
    }

    private _getContentLink(info: IContentInfo, service: IVCNavigationService) {
        let lineStyle = "plain";
        if (info.isError) {
            lineStyle = "error";
        }
        const gitRepositoryLink = service.getRepositoryUrl(info.repositoryName, undefined, undefined, undefined, {
            line: info.lineNumber,
            lineStyle: lineStyle,
            lineTooltip: info.lineTooltip,
            version: this._getGitVersionSpec(info.version),
            path: info.path,
            _a: "contents"
        });
        return gitRepositoryLink;
    }

    private _getGitVersionSpec(version: string): string {
        if (!startsWith(version, "GC")) {
            version = "GC" + version;
        }

        return version;
    }
}
